import { useTranslation } from 'react-i18next';
import { ApiError } from '@/resources/http';

export function useErrorText(): (error: unknown) => string {
  const { t } = useTranslation();

  return (error: unknown) => {
    if (!(error instanceof ApiError)) {
      return error instanceof Error ? t('errors.offline') : t('errors.unknown');
    }

    switch (error.message) {
      case 'Missing or invalid Authorization header':
        return t('errors.signInRequired');
      case 'Invalid token':
      case 'Invalid or expired token':
        return t('errors.sessionExpired');
      case 'Invalid email or password':
        return t('errors.badCredentials');
      case 'Email already registered':
        return t('errors.emailTaken');
      case 'Validation failed':
        return t('errors.validation');
      case 'User not found':
        return t('errors.userGone');
      case 'Organization not found':
        return t('errors.organizationNotFound');
      case 'Invite not found':
        return t('errors.inviteNotFound');
      case 'Member not found':
        return t('errors.memberNotFound');
      case 'Only owners can do this':
        return t('errors.ownersOnly');
      case 'Only owners can remove members':
        return t('errors.ownersOnlyRemove');
      case 'Friends cannot see this':
        return t('errors.friendsNoAccess');
      case 'An organization needs at least one owner':
        return t('errors.lastOwner');
      case 'Reminder not found':
        return t('errors.reminderNotFound');
      case 'Debt not found':
        return t('errors.debtNotFound');
      case 'Payment not found':
        return t('errors.paymentNotFound');
      case 'Installment plan needs an amount and a due day':
        return t('errors.installmentIncomplete');
      case 'Only owners can assign debts to someone else':
        return t('errors.debtorOwnersOnly');
      case 'Debtor is not a member of this organization':
        return t('errors.debtorNotMember');
      case 'Assignee is not a member of this organization':
        return t('errors.assigneeNotMember');
      case 'Recipient is not a member of this organization':
        return t('errors.recipientNotMember');
      case 'You cannot nudge yourself':
        return t('errors.nudgeSelf');
      case 'Recipient has notifications turned off':
        return t('errors.recipientNoPush');
      case 'Push delivery failed':
        return t('errors.pushDeliveryFailed');
      case 'Debt is already settled':
        return t('errors.debtSettled');
      case 'Debt has no debtor':
        return t('errors.debtNoDebtor');
      case 'Nothing to update':
        return t('errors.nothingToUpdate');
      case 'Attachment not found':
        return t('errors.attachmentNotFound');
      case 'Attachment limit reached':
        return t('errors.attachmentLimit');
      case 'File is too large':
        return t('errors.attachmentTooLarge');
      case 'No file uploaded':
      case 'Upload rejected':
        return t('errors.attachmentRejected');
      case 'Unsupported image type':
        return t('errors.avatarType');
      case 'Photo not found':
        return t('errors.photoNotFound');
      case 'Photo limit reached':
        return t('errors.photoLimit');
      case 'Only owners can delete photos from others':
        return t('errors.photoOwnersOnly');
      case 'Unsupported media type':
        return t('errors.mediaType');
      case 'Album not found':
        return t('errors.albumNotFound');
      case 'Share not found':
        return t('errors.shareNotFound');
      case 'Share needs an album or photos':
        return t('errors.shareEmpty');
      case 'File storage is not configured':
        return t('errors.storageUnavailable');
      case 'Internal Server Error':
        return t('errors.serverError');
      default:
        return t('errors.withStatus', { status: error.status });
    }
  };
}
